import React from 'react';
import PropTypes from 'prop-types';
import { t } from '@insight/toolkit-utils/lib/labels';
import {
  checkForGuestCheckoutCookie,
  deleteGuestCheckoutCookie,
} from '@insight/toolkit-utils/lib/helpers/cookieHelpers';
import { getCurrentLocale } from '@insight/toolkit-utils';
import Button from '../Button/Button';
import ButtonGroup from '../ButtonGroup/ButtonGroup';
import Currency from '../Currency/Currency';

import Modal from '../Modal/Modal';

import { AddToCartModalTabs } from './AddToCartModalTabs';
import AddToCartModalResource from './AddToCartModalResource';

export const AddToCartModal = ({
  currencyCode,
  isLoggedOutDefault,
  isOpen,
  products,
  onClose,
  hideTabs,
  showDividers,
  accessories,
  protection,
  thirdPartyProtection,
  quantity = 1,
  stock,
  addToCart,
  showQuantityExceed = false,
  showPrice,
}) => {
  if (!isOpen || (!products && !products.length)) return null;
  // Get sub total
  let subTotal = 0;
  const locale = getCurrentLocale('insight_locale');
  const isShoppingCartEnabled =
    window?.flags?.['GNA-10394-SHOPPING-CART'] &&
    isLoggedOutDefault &&
    locale === 'en_US';

  const guestCheckoutCookieHandler = () => {
    if (isLoggedOutDefault) {
      checkForGuestCheckoutCookie('guest-checkout-enabled');
    } else {
      deleteGuestCheckoutCookie('guest-checkout-enabled');
      window.location = isShoppingCartEnabled
        ? '/insightweb/cart'
        : '/insightweb/viewCart';
    }
  };

  const isLoggedIn = !isLoggedOutDefault;
  const newWebPriceFeature =
    window.flags && window.flags['GNA-11926-NEW-WEB-PRICING'];

  const getWebPrice = (product) => {
    if (newWebPriceFeature) {
      if (product?.price?.webPrice != null) {
        return product?.price?.webPrice;
      }

      return product.price?.price;
    }
    return isLoggedIn
      ? product?.insightPrice ||
          product?.price?.insightPrice ||
          product.price?.price
      : product?.listPrice || product?.price?.listPrice || product.price?.price;
  };

  const renderProducts = () => {
    if (!products && !products.length) return null;
    return products.map((product) => {
      // Needed because the placement API returns a difference product structure
      const name =
        (product &&
          product.descriptions &&
          product.descriptions.shortDescription) ||
        product.description;
      const price = getWebPrice(product);
      const image =
        (product &&
          product.images &&
          product.images[0] &&
          product.images[0].images &&
          product.images[0].images.MD &&
          product.images[0].images.MD[0] &&
          product.images[0].images.MD[0].url) ||
        product.image;
      const mrfPart =
        (product && product.manufacturerPartNumber) ||
        (product && product.manufacturer && product.manufacturer.partNumber);

      // increment subTotal
      subTotal += price * quantity;
      return (
        <AddToCartModalResource
          key={product.materialId}
          categoryType={product.categoryType}
          currencyCode={currencyCode}
          hideImage={!image}
          imageUrl={image}
          imageDescription={name}
          shortDescription={name}
          insightPart={product.materialId}
          mfrPart={mrfPart}
          quantity={quantity}
          stock={stock}
          insightPrice={price}
          showDividers={showDividers}
          showQuantityExceed={showQuantityExceed}
          isLoggedIn={isLoggedIn}
          newWebPriceFeature={newWebPriceFeature}
          showPrice={showPrice}
        />
      );
    });
  };
  const renderTabs = () => {
    if (hideTabs) return null;

    if (
      !accessories &&
      !protection &&
      (!thirdPartyProtection || !thirdPartyProtection.length)
    )
      return null;

    return (
      <section className="c-product-tabs">
        <AddToCartModalTabs
          accessories={accessories?.prodList || []}
          protection={protection?.prodList || []}
          thirdPartyProtection={thirdPartyProtection || []}
          addToCart={addToCart}
          showPrice={showPrice}
        />
      </section>
    );
  };
  return (
    <div>
      <Modal className="c-add-to-cart" isOpen onClose={onClose} size="medium">
        <Modal.Header onClick={onClose}>{t('Added to cart')}</Modal.Header>
        <Modal.Body id="added-to-cart">
          <section>
            {renderProducts()}
            <div>
              {showPrice && (
                <div className="c-add-to-cart__subtotal">
                  <span>{t('Subtotal')}: </span>
                  <Currency currencyCode={currencyCode} value={subTotal} />
                </div>
              )}
              <div>
                <ButtonGroup align="right">
                  <Button size="small" color="secondary" onClick={onClose}>
                    {t('Continue Shopping')}
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => guestCheckoutCookieHandler()}
                  >
                    {t('Continue to checkout')}
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          </section>
          {renderTabs()}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AddToCartModal;

AddToCartModal.propTypes = {
  currencyCode: PropTypes.string.isRequired,
  isLoggedOutDefault: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  products: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  hideTabs: PropTypes.bool,
  showDividers: PropTypes.bool,
  accessories: PropTypes.object,
  protection: PropTypes.object,
  thirdPartyProtection: PropTypes.array,
  quantity: PropTypes.number,
  stock: PropTypes.number,
  addToCart: PropTypes.func.isRequired,
  showQuantityExceed: PropTypes.bool.isRequired,
  showPrice: PropTypes.bool,
};

AddToCartModal.defaultProps = {
  hideTabs: false,
  showDividers: false,
  accessories: null,
  protection: null,
  thirdPartyProtection: null,
  quantity: 1,
  showPrice: true,
};
